import Module from 'manifold-3d';
import { Vector3 } from 'three';
import { SweepMesh, mergeMeshes } from '../geometry/sweep';
import { cleanupMeshForStl } from './meshCleanup';

// Minimal structural typing for the WASM API we use (manifold-3d versions vary).
export interface ManifoldSolid {
  delete(): void;
  simplify(tolerance: number): ManifoldSolid;
  getMesh(): {
    numProp: number;
    vertProperties: Float32Array;
    triVerts: Uint32Array;
  };
  volume(): number;
  splitByPlane(normal: readonly [number, number, number], originOffset: number): [ManifoldSolid, ManifoldSolid];
}

interface ManifoldMeshGl {
  merge(): boolean;
}

interface WasmApi {
  setup: () => void;
  Manifold: {
    ofMesh(mesh: ManifoldMeshGl): ManifoldSolid;
    union(manifolds: readonly ManifoldSolid[]): ManifoldSolid;
    difference(a: ManifoldSolid, b: ManifoldSolid): ManifoldSolid;
  };
  Mesh: new (options: {
    numProp: number;
    vertProperties: Float32Array;
    triVerts: Uint32Array;
  }) => ManifoldMeshGl;
}

let wasmPromise: Promise<WasmApi> | null = null;
let wasmApi: WasmApi | null = null;

/** Lazy-init manifold WASM (browser + Node). */
export async function ensureStlFixer(): Promise<void> {
  if (wasmApi) return;
  if (!wasmPromise) {
    wasmPromise = (async () => {
      const wasm = (await Module()) as unknown as WasmApi;
      wasm.setup();
      return wasm;
    })();
  }
  wasmApi = await wasmPromise;
}

/** Build a Manifold solid from a sweep mesh, or null if non-manifold. */
export async function sweepToManifold(mesh: SweepMesh): Promise<ManifoldSolid | null> {
  await ensureStlFixer();
  const { Manifold, Mesh } = wasmApi!;
  return tryOfMesh(mesh, Mesh, Manifold);
}

/** Union many sweeps into one solid (pairwise tree). Deletes nothing from inputs (meshes). */
export async function unionSweeps(
  meshes: SweepMesh[],
): Promise<{ solid: ManifoldSolid | null; skipped: number; warning?: string }> {
  await ensureStlFixer();
  const { Manifold, Mesh } = wasmApi!;
  const solids: ManifoldSolid[] = [];
  let skipped = 0;
  for (const mesh of meshes) {
    if (mesh.indices.length < 3) {
      skipped += 1;
      continue;
    }
    const s = tryOfMesh(mesh, Mesh, Manifold);
    if (s) solids.push(s);
    else skipped += 1;
  }
  if (solids.length === 0) {
    return { solid: null, skipped, warning: 'no manifold pieces to union' };
  }
  try {
    let combined = solids[0];
    for (let i = 1; i < solids.length; i++) {
      const next = Manifold.union([combined, solids[i]]);
      combined.delete();
      solids[i].delete();
      combined = next;
    }
    return {
      solid: combined,
      skipped,
      warning: skipped > 0 ? `skipped ${skipped} non-manifold piece(s)` : undefined,
    };
  } catch (err) {
    for (const s of solids) {
      try {
        s.delete();
      } catch {
        /* ignore */
      }
    }
    return {
      solid: null,
      skipped,
      warning: `union failed: ${err instanceof Error ? err.message : err}`,
    };
  }
}

/**
 * Split a solid by a plane through `point` with unit `normal`.
 * Returns [positive side (along +normal), negative side]. Caller owns both.
 */
export function splitSolidByPlane(
  solid: ManifoldSolid,
  normal: Vector3,
  point: Vector3,
): [ManifoldSolid, ManifoldSolid] {
  const n = normal.clone().normalize();
  const offset = n.dot(point);
  return solid.splitByPlane([n.x, n.y, n.z], offset);
}

export function manifoldToSweepMesh(man: ManifoldSolid): SweepMesh {
  return manifoldToSweep(man);
}

/**
 * Boolean-union overlapping print shells into one manifold solid, optionally
 * subtract female socket cavities (so click-fit holes stay open), then simplify.
 */
export async function fixPrintSolid(
  pieces: SweepMesh[],
  opts?: { subtract?: SweepMesh[] },
): Promise<{
  mesh: SweepMesh;
  fixed: boolean;
  warning?: string;
}> {
  if (pieces.length === 0) {
    return {
      mesh: { positions: new Float32Array(), normals: new Float32Array(), indices: new Uint32Array() },
      fixed: false,
      warning: 'Empty mesh',
    };
  }

  await ensureStlFixer();
  const { Manifold, Mesh } = wasmApi!;

  const solids: ManifoldSolid[] = [];
  let skipped = 0;
  const warnings: string[] = [];

  try {
    for (const piece of pieces) {
      if (piece.indices.length < 3) {
        skipped += 1;
        continue;
      }
      const solid = tryOfMesh(piece, Mesh, Manifold);
      if (solid) solids.push(solid);
      else skipped += 1;
    }

    if (solids.length === 0) {
      return {
        mesh: cleanupMeshForStl(mergeMeshes(pieces)).mesh,
        fixed: false,
        warning: 'STL fixer could not build a manifold solid; exported cleaned mesh only',
      };
    }

    let combined = solids.length === 1 ? solids[0] : Manifold.union(solids);
    if (solids.length > 1) {
      for (const s of solids) s.delete();
    }

    for (const cutter of opts?.subtract ?? []) {
      if (cutter.indices.length < 3) continue;
      const cavity = tryOfMesh(cutter, Mesh, Manifold);
      if (!cavity) {
        warnings.push('socket cavity cutter skipped (non-manifold)');
        continue;
      }
      const next = Manifold.difference(combined, cavity);
      combined.delete();
      cavity.delete();
      combined = next;
    }

    // Simplify for a cleaner, smaller STL (tolerance in world metres ≈ 0.08 mm at 1:100).
    const simplified = combined.simplify(0.008);
    if (simplified !== combined) {
      combined.delete();
      combined = simplified;
    }

    const out = manifoldToSweep(combined);
    combined.delete();

    if (out.indices.length < 3) {
      return {
        mesh: cleanupMeshForStl(mergeMeshes(pieces)).mesh,
        fixed: false,
        warning: 'STL fixer produced an empty solid; exported cleaned mesh only',
      };
    }

    if (skipped > 0) warnings.unshift(`STL fixer skipped ${skipped} non-manifold piece(s)`);

    return {
      mesh: out,
      fixed: true,
      warning: warnings.length > 0 ? warnings.join('; ') : undefined,
    };
  } catch (err) {
    return {
      mesh: cleanupMeshForStl(mergeMeshes(pieces)).mesh,
      fixed: false,
      warning: `STL fixer failed: ${err instanceof Error ? err.message : err}`,
    };
  }
}

/**
 * Make face windings consistent across shared edges (required by Manifold.ofMesh).
 * Returns null if the surface is non-orientable or not a closed 2-manifold.
 */
export function orientMeshOutward(mesh: SweepMesh): SweepMesh | null {
  const triCount = Math.floor(mesh.indices.length / 3);
  if (triCount === 0) return null;

  const edgeFaces = new Map<string, number[]>();
  const edgeKey = (a: number, b: number) => (a < b ? `${a}_${b}` : `${b}_${a}`);

  for (let t = 0; t < triCount; t++) {
    const a = mesh.indices[t * 3];
    const b = mesh.indices[t * 3 + 1];
    const c = mesh.indices[t * 3 + 2];
    for (const [u, v] of [
      [a, b],
      [b, c],
      [c, a],
    ] as const) {
      const k = edgeKey(u, v);
      const list = edgeFaces.get(k);
      if (list) list.push(t);
      else edgeFaces.set(k, [t]);
    }
  }

  for (const faces of edgeFaces.values()) {
    if (faces.length !== 2) return null;
  }

  const flip = new Uint8Array(triCount);
  const seen = new Uint8Array(triCount);
  const queue: number[] = [0];
  seen[0] = 1;

  const hasDirected = (t: number, u: number, v: number): boolean => {
    let a = mesh.indices[t * 3];
    let b = mesh.indices[t * 3 + 1];
    let c = mesh.indices[t * 3 + 2];
    if (flip[t]) {
      const tmp = b;
      b = c;
      c = tmp;
    }
    return (a === u && b === v) || (b === u && c === v) || (c === u && a === v);
  };

  while (queue.length > 0) {
    const t = queue.pop()!;
    let a = mesh.indices[t * 3];
    let b = mesh.indices[t * 3 + 1];
    let c = mesh.indices[t * 3 + 2];
    if (flip[t]) {
      const tmp = b;
      b = c;
      c = tmp;
    }
    for (const [u, v] of [
      [a, b],
      [b, c],
      [c, a],
    ] as const) {
      const pair = edgeFaces.get(edgeKey(u, v))!;
      const other = pair[0] === t ? pair[1] : pair[0];
      if (!seen[other]) {
        seen[other] = 1;
        if (!hasDirected(other, v, u)) flip[other] = 1;
        if (!hasDirected(other, v, u)) return null;
        queue.push(other);
      } else if (!hasDirected(other, v, u)) {
        return null;
      }
    }
  }

  for (let i = 0; i < triCount; i++) {
    if (!seen[i]) return null;
  }

  // Prefer positive volume (outward normals).
  let volume6 = 0;
  for (let t = 0; t < triCount; t++) {
    let a = mesh.indices[t * 3];
    let b = mesh.indices[t * 3 + 1];
    let c = mesh.indices[t * 3 + 2];
    if (flip[t]) {
      const tmp = b;
      b = c;
      c = tmp;
    }
    const ax = mesh.positions[a * 3];
    const ay = mesh.positions[a * 3 + 1];
    const az = mesh.positions[a * 3 + 2];
    const bx = mesh.positions[b * 3];
    const by = mesh.positions[b * 3 + 1];
    const bz = mesh.positions[b * 3 + 2];
    const cx = mesh.positions[c * 3];
    const cy = mesh.positions[c * 3 + 1];
    const cz = mesh.positions[c * 3 + 2];
    volume6 += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx);
  }
  const invert = volume6 < 0;

  const indices = new Uint32Array(mesh.indices.length);
  for (let t = 0; t < triCount; t++) {
    const a = mesh.indices[t * 3];
    const b = mesh.indices[t * 3 + 1];
    const c = mesh.indices[t * 3 + 2];
    const doFlip = invert ? !flip[t] : !!flip[t];
    if (doFlip) {
      indices[t * 3] = a;
      indices[t * 3 + 1] = c;
      indices[t * 3 + 2] = b;
    } else {
      indices[t * 3] = a;
      indices[t * 3 + 1] = b;
      indices[t * 3 + 2] = c;
    }
  }

  return { positions: mesh.positions, normals: mesh.normals, indices };
}

/**
 * Weld coincident positions and drop degenerates, keeping shared topology.
 * Unlike {@link cleanupMeshForStl}, this does not explode facets into unique verts
 * (required for Manifold.ofMesh).
 */
export function weldMeshForManifold(mesh: SweepMesh, eps = 1e-7): SweepMesh {
  const n = mesh.positions.length / 3;
  const remap = new Int32Array(n);
  const positions: number[] = [];
  const keyToId = new Map<string, number>();
  const inv = 1 / eps;

  for (let i = 0; i < n; i++) {
    const x = mesh.positions[i * 3];
    const y = mesh.positions[i * 3 + 1];
    const z = mesh.positions[i * 3 + 2];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      remap[i] = -1;
      continue;
    }
    const key = `${Math.round(x * inv)},${Math.round(y * inv)},${Math.round(z * inv)}`;
    let id = keyToId.get(key);
    if (id === undefined) {
      id = positions.length / 3;
      keyToId.set(key, id);
      positions.push(x, y, z);
    }
    remap[i] = id;
  }

  const indices: number[] = [];
  for (let t = 0; t < mesh.indices.length; t += 3) {
    const a = remap[mesh.indices[t]];
    const b = remap[mesh.indices[t + 1]];
    const c = remap[mesh.indices[t + 2]];
    if (a < 0 || b < 0 || c < 0 || a === b || b === c || c === a) continue;
    const ax = positions[a * 3];
    const ay = positions[a * 3 + 1];
    const az = positions[a * 3 + 2];
    const bx = positions[b * 3];
    const by = positions[b * 3 + 1];
    const bz = positions[b * 3 + 2];
    const cx = positions[c * 3];
    const cy = positions[c * 3 + 1];
    const cz = positions[c * 3 + 2];
    const ux = bx - ax;
    const uy = by - ay;
    const uz = bz - az;
    const vx = cx - ax;
    const vy = cy - ay;
    const vz = cz - az;
    const nx = uy * vz - uz * vy;
    const ny = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    if (!(Math.hypot(nx, ny, nz) > 1e-18)) continue;
    indices.push(a, b, c);
  }

  return {
    positions: new Float32Array(positions),
    normals: new Float32Array(positions.length),
    indices: Uint32Array.from(indices),
  };
}

/**
 * True when the mesh is a closed 2-manifold solid with positive volume
 * (after weld + orient, via Manifold.ofMesh).
 */
export async function checkManifoldSolid(
  mesh: SweepMesh,
): Promise<{ ok: true; volume: number } | { ok: false; reason: string }> {
  if (mesh.indices.length < 3) {
    return { ok: false, reason: 'empty mesh' };
  }
  await ensureStlFixer();
  const { Manifold, Mesh } = wasmApi!;
  const solid = tryOfMesh(mesh, Mesh, Manifold);
  if (!solid) {
    return { ok: false, reason: 'not a closed 2-manifold' };
  }
  try {
    const volume = solid.volume();
    if (!(volume > 0)) {
      return { ok: false, reason: `non-positive volume (${volume})` };
    }
    return { ok: true, volume };
  } finally {
    solid.delete();
  }
}

function tryOfMesh(
  piece: SweepMesh,
  Mesh: WasmApi['Mesh'],
  Manifold: WasmApi['Manifold'],
): ManifoldSolid | null {
  const welded = weldMeshForManifold(piece);
  if (welded.indices.length < 3) return null;
  const oriented = orientMeshOutward(welded) ?? welded;

  try {
    const gl = new Mesh({
      numProp: 3,
      vertProperties: new Float32Array(oriented.positions),
      triVerts: new Uint32Array(oriented.indices),
    });
    return Manifold.ofMesh(gl);
  } catch {
    try {
      const gl = new Mesh({
        numProp: 3,
        vertProperties: new Float32Array(piece.positions),
        triVerts: new Uint32Array(piece.indices),
      });
      if (!gl.merge()) return null;
      return Manifold.ofMesh(gl);
    } catch {
      return null;
    }
  }
}

function manifoldToSweep(man: ManifoldSolid): SweepMesh {
  const gl = man.getMesh();
  const numProp = gl.numProp || 3;
  const nVert = gl.vertProperties.length / numProp;
  const positions = new Float32Array(nVert * 3);
  for (let i = 0; i < nVert; i++) {
    positions[i * 3] = gl.vertProperties[i * numProp];
    positions[i * 3 + 1] = gl.vertProperties[i * numProp + 1];
    positions[i * 3 + 2] = gl.vertProperties[i * numProp + 2];
  }
  const indices = new Uint32Array(gl.triVerts);
  const normals = new Float32Array(nVert * 3);
  // Explode to facet normals for STL encode path.
  return cleanupMeshForStl({ positions, normals, indices }).mesh;
}
